// src/hooks/useDoctorFollow.js
"use client";
import { useCallback, useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { followDoctorThunk, unfollowDoctorThunk, getFollowedDoctorsThunk } from "../redux/thunks/followsThunks";
import { axiosDoctorRegister, axiosUsers } from "../../api/axios";
import Cookies from "js-cookie";

const toStr = (v) => (v === null || v === undefined ? "" : String(v).trim());
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const looksLikeUuid = (s) => typeof s === "string" && UUID_RE.test(s);
const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));

const collectTargetIds = (target) => {
  if (!target) return [];
  if (typeof target === "string" || typeof target === "number") {
    return [toStr(target)];
  }
  return uniq([
    toStr(target.id),
    toStr(target._id),
    toStr(target.userId),
    toStr(target.doctorId),
    toStr(target.patientId),
    toStr(target.user?.id),
    toStr(target.accountId),
  ]);
};

const collectFollowIds = (f) => {
  if (!f) return [];
  return uniq([
    toStr(f.followeeId),
    toStr(f.followee_id),
    toStr(f.doctorId),
    toStr(f.doctor_id),
    toStr(f.patientId),
    toStr(f.patient_id),
    toStr(f.followee?.id),
    toStr(f.followee?.doctorId),
    toStr(f.followee?.patientId),
    toStr(f.followee?.userId),
    toStr(f.doctor?.id),
    toStr(f.patient?.id),
    toStr(f.followedDoctorId),
    toStr(f.followedDoctor?.id),
    toStr(f.followedPatientId),
    toStr(f.followedPatient?.id),
    toStr(f.id),
    toStr(f._id),
  ]);
};

const intersects = (a, b) => {
  const setB = new Set(b);
  return a.some((x) => setB.has(x));
};

const chooseBestId = (ids) => {
  if (!ids || ids.length === 0) return "";
  const uuid = ids.find((x) => looksLikeUuid(x));
  if (uuid) return uuid;
  const sorted = [...ids].sort((x, y) => (y?.length || 0) - (x?.length || 0));
  return sorted[0] || "";
};

/**
 * Devine si la cible passée au hook est un "doctor" ou un "patient".
 * Un `explicitType` fourni par l'appelant a toujours priorité.
 */
const inferFolloweeType = (target, explicitType) => {
  if (explicitType === "doctor" || explicitType === "patient") return explicitType;
  if (!target || typeof target === "string" || typeof target === "number") {
    return "doctor"; // comportement historique par défaut
  }
  if (target.role === "patient" || (target.patientId && !target.doctorId)) return "patient";
  if (target.role === "doctor" || target.doctorId || target.specialty) return "doctor";
  return "doctor";
};

/**
 * ✅ Récupère directement l'utilisateur connecté depuis l'API.
 *
 * Le cookie patient "accessToken" est httpOnly : js-cookie ne peut jamais le
 * lire côté client (il reste `undefined`), mais le navigateur l'envoie quand
 * même automatiquement grâce à `withCredentials: true`.
 *
 * On cherche AUSSI les clés de token docteur
 * ("doctorAccessToken" / "doctorToken"), pour que le rôle "doctor" soit
 * correctement détecté.
 */
const fetchCurrentUserId = async () => {
  const doctorToken =
    Cookies.get("doctorAccessToken") ||
    Cookies.get("doctorToken") ||
    localStorage.getItem("doctorAccessToken") ||
    localStorage.getItem("doctorToken");

  const patientToken =
    Cookies.get("accessToken") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("authToken");

  const storedRole = localStorage.getItem("userRole");

  if (doctorToken || storedRole === "doctor") {
    try {
      const doctorRes = await axiosDoctorRegister.get("/me", {
        headers: doctorToken ? { Authorization: `Bearer ${doctorToken}` } : undefined,
        withCredentials: true,
      });

      const doctorId =
        doctorRes.data?.data?.id ||
        doctorRes.data?.id ||
        doctorRes.data?._id;

      if (doctorId) return { id: String(doctorId), role: "doctor" };
    } catch {
      // on retombe sur la tentative patient ci-dessous
    }
  }

  try {
    const userRes = await axiosUsers.get("/me", {
      headers: patientToken ? { Authorization: `Bearer ${patientToken}` } : undefined,
      withCredentials: true,
    });

    const userId = userRes.data?.data?.id || userRes.data?.id || userRes.data?._id;
    if (userId) {
      return { id: String(userId), role: "user" };
    }
  } catch (err) {
    console.error("❌ Patient endpoint also failed:", err);
  }

  return null;
};

/**
 * @param {object|string} targetDoctor - l'entité suivie (docteur OU patient) ou son id
 * @param {"doctor"|"patient"} [followeeTypeOverride] - force le type de la cible
 *        (par défaut déduit automatiquement, donc rétro-compatible avec
 *        tous les appels existants `useDoctorFollow(doctor)`)
 */
export const useDoctorFollow = (targetDoctor, followeeTypeOverride) => {
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.auth || {});
  const storePatientUser = authState.currentUser || authState.user || null;

  const storeDoctorUser = useSelector((state) => state.doctor?.user || null);

  const storeUser = storeDoctorUser || storePatientUser;
  const storeRole =
    (storeDoctorUser ? "doctor" : null) ||
    authState.role ||
    storePatientUser?.role ||
    localStorage.getItem("userRole");

  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadUser = async () => {
      const storedId =
        storeDoctorUser?.id ||
        storeDoctorUser?.doctorId ||
        storeDoctorUser?._id ||
        storePatientUser?.id ||
        storePatientUser?._id ||
        storePatientUser?.userId;

      const userInfo = storedId
        ? { id: String(storedId), role: storeRole || "user" }
        : await fetchCurrentUserId();

      if (isMounted) {
        setCurrentUserInfo(userInfo);
        setInitialLoading(false);

        if (userInfo?.id) {
          dispatch(
            getFollowedDoctorsThunk({
              followerId: String(userInfo.id),
              followeeType: null,
            }),
          );
        }
      }
    };

    loadUser();

    return () => {
      isMounted = false;
    };
  }, [dispatch, storeDoctorUser, storePatientUser, storeRole]);

  const reduxUser = storeUser;
  const currentUserId =
    currentUserInfo?.id ||
    reduxUser?.id ||
    reduxUser?._id ||
    reduxUser?.doctorId ||
    reduxUser?.patientId ||
    reduxUser?.userId;

  const rawRole =
    currentUserInfo?.role ||
    (storeDoctorUser ? "doctor" : null) ||
    authState.role ||
    reduxUser?.role ||
    (reduxUser ? "user" : null);
  const currentRole = rawRole === "doctor" ? "doctor" : rawRole ? "patient" : null;

  const followedDoctors = useSelector((s) => {
    const follows = s.follows?.followedDoctors || [];
    return follows;
  });

  const globalLoading = useSelector((s) => !!s.follows?.loading);

  const targetIds = useMemo(() => {
    const ids = collectTargetIds(targetDoctor);
    return ids;
  }, [targetDoctor]);

  const followeeIdToSend = useMemo(() => chooseBestId(targetIds), [targetIds]);

  const followeeType = useMemo(
    () => inferFolloweeType(targetDoctor, followeeTypeOverride),
    [targetDoctor, followeeTypeOverride]
  );

  const storeIsFollowing = useMemo(() => {
    if (!targetIds.length) {
      return false;
    }

    const result = followedDoctors.some((f) => {
      const storedType = f?.followeeType === "user" ? "patient" : f?.followeeType;
      if (storedType && storedType !== followeeType) return false;
      const followIds = collectFollowIds(f);
      return intersects(targetIds, followIds);
    });

    return result;
  }, [followedDoctors, targetIds]);

  const [optimistic, setOptimistic] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    setOptimistic(null);
  }, [storeIsFollowing, followeeIdToSend]);

  const isFollowing = optimistic !== null ? optimistic : storeIsFollowing;

  // ✅ Règle métier front (à garder cohérente avec le backend, si le
  // backend l'impose aussi) : un patient ne peut suivre qu'un docteur.
  // Un docteur peut suivre un docteur OU un patient.
  const roleAllowsThisFollow = useMemo(() => {
    if (!currentRole) return false;
    if (currentRole === "patient" && followeeType !== "doctor") return false;
    return true;
  }, [currentRole, followeeType]);

  const canFollow = useMemo(() => {
    if (!currentUserId) return false;
    if (!currentRole) return false;
    if (!followeeIdToSend) return false;
    if (!roleAllowsThisFollow) return false;
    if (toStr(currentUserId) === toStr(followeeIdToSend)) return false;
    return true;
  }, [currentUserId, currentRole, followeeIdToSend, roleAllowsThisFollow]);

  const toggleFollow = useCallback(async () => {
    if (!canFollow) {
      console.error("❌ Cannot follow", { currentRole, followeeType, roleAllowsThisFollow });
      return;
    }

    const next = !isFollowing;
    setOptimistic(next);
    setLocalLoading(true);

    try {
      if (next) {
        // ⚠️ On n'envoie JAMAIS `followerType` : le backend le rejette
        // ("property followerType should not exist"). Seuls followerId,
        // followeeId et followeeType sont acceptés par le DTO.
        await dispatch(
          followDoctorThunk({
            followerId: String(currentUserId),
            followeeId: String(followeeIdToSend),
            followeeType,
          })
        ).unwrap();
      } else {
        await dispatch(
          unfollowDoctorThunk({
            followerId: String(currentUserId),
            followeeId: String(followeeIdToSend),
            followeeType,
          })
        ).unwrap();
      }
      await dispatch(
        getFollowedDoctorsThunk({
          followerId: String(currentUserId),
          followeeType: null,
        }),
      ).unwrap();

      setOptimistic(null);
    } catch (e) {
      console.error("❌ Toggle follow error:", e);
      setOptimistic(!next);
      setTimeout(() => setOptimistic(null), 0);
    } finally {
      setLocalLoading(false);
    }
  }, [dispatch, canFollow, currentUserId, followeeIdToSend, followeeType, isFollowing]);

  return {
    isFollowing,
    loading: localLoading || globalLoading || initialLoading,
    statusReady: !initialLoading,
    toggleFollow,
    canFollow,
    role: currentRole,
    followeeType,
    currentUserId,
  };
};

/**
 * ✅ À utiliser sur une carte "patient" pour permettre à un docteur de
 * suivre un patient. `canFollow` sera automatiquement `false` si
 * l'utilisateur connecté est un patient (règle métier ci-dessus).
 */
export const usePatientFollow = (targetPatient) => useDoctorFollow(targetPatient, "patient");